import { TYPE_LABEL, moduleLabel, formatDate, openOverlaps } from '../format.js';
import { LIFECYCLE_LABEL, LIFECYCLE_HELP } from '../lifecycle.js';
import { navigate, taskPath } from '../OrbitDevelopmentApp.jsx';

/**
 * A field edited in place, from the list.
 *
 * Module, type and status are the three that change without anything else changing with them —
 * a task moves to In progress, it does not also acquire a description. Opening the task to say
 * so, saving, and coming back was the whole cost of that, paid on every one.
 *
 * It stops propagation on click because the row underneath opens the task: choosing a value
 * must not also navigate away from the list you chose it in.
 */
function CellSelect({ label, value, choices, disabled, onChange, style }) {
  return (
    <select
      className="o-cell-sel"
      aria-label={label}
      value={value ?? ''}
      disabled={disabled}
      style={style}
      title={choices.find(c => c.id === value)?.label || undefined}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onChange={e => { e.stopPropagation(); onChange(e.target.value); }}
    >
      {choices.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
    </select>
  );
}

/**
 * One task row. The columns live here and nowhere else, so every section renders the same table.
 *
 * The whole row is the click target, which is why the grip stops propagation: picking a task up
 * must not also open it.
 */
export default function TaskRow({
  task, options, overlaps, draggable, dragging, dropEdge, sortedOff, saving,
  onDragStart, onDragOver, onDragEnd, onDrop, onPatch, onTransition,
}) {
  const shared = openOverlaps(task, overlaps);

  const modules = options?.modules || [];
  const types   = (options?.types || []).map(t => ({ id: t, label: TYPE_LABEL[t] || t }));

  // The status choices are the CURRENT status plus the moves this caller may make from it —
  // the server sends that set, already narrowed by role. So the column is editable in place
  // like the two beside it, and still cannot express a move that does not exist: there is no
  // list of every status to pick the wrong one out of.
  const status = task.lifecycle_status;
  const statusChoices = [status, ...(options?.transitions?.[status] || [])]
    .map(s => ({ id: s, label: LIFECYCLE_LABEL[s] || s }));

  return (
    <tr
      style={{ cursor: 'pointer' }}
      data-dragging={dragging ? 'true' : undefined}
      data-drop={dropEdge || undefined}
      onClick={() => navigate(taskPath(task.id))}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <td
        className="o-grip"
        draggable={draggable}
        aria-disabled={draggable ? undefined : 'true'}
        title={sortedOff
          ? 'Sorting is on. Switch to Manual order to rearrange.'
          : 'Drag to move this task'}
        onClick={e => e.stopPropagation()}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        &#8942;&#8942;
      </td>
      {/* Everything about the task on one line. The area used to sit on a second line under the
          title, which doubled the height of every row that had one — it says where the task is,
          which reads just as well after it. */}
      <td title={task.title}>
        <span className="o-clip">
          <span className="o-strong">{task.title}</span>
          {task.area && (
            <span className="o-note" style={{ marginLeft: 8, marginBottom: 0 }}>{task.area}</span>
          )}
          {shared.length > 0 && (
            <span className="o-badge" style={{ marginLeft: 8 }}
              title={`Shares files with: ${shared.map(o => o.title).join(', ')}`}>
              Shared files
            </span>
          )}
          {Number(task.screenshot_count) > 0 && (
            <span className="o-note" style={{ marginLeft: 8, marginBottom: 0 }}>
              {task.screenshot_count} attachment{Number(task.screenshot_count) === 1 ? '' : 's'}
            </span>
          )}
        </span>
      </td>
      <td title={moduleLabel(options, task.target_module)}>
        <CellSelect label={`Module for ${task.title}`} value={task.target_module}
          choices={modules} disabled={saving}
          onChange={v => onPatch(task, { target_module: v })} />
      </td>
      <td>
        <CellSelect label={`Type of ${task.title}`} value={task.task_type}
          choices={types} disabled={saving}
          onChange={v => onPatch(task, { task_type: v })} />
      </td>
      {/* Status changes like the two above it, but through the transition route rather than a
          PATCH: a move is owned by a role and has to leave a row in the log. Where the caller
          owns no move from here, the select holds one option and is inert — which reads as
          "not yours to move" without a second kind of cell. */}
      <td title={LIFECYCLE_HELP[status]}>
        <CellSelect label={`Status of ${task.title}`} value={status}
          choices={statusChoices} disabled={saving || statusChoices.length === 1}
          onChange={v => onTransition(task, v)} />
        {Number(task.rejection_count) > 0 && (
          <span className="o-num" style={{ marginLeft: 4, color: 'var(--error)' }}
            title={`Sent back by QA ${task.rejection_count} time`
              + `${Number(task.rejection_count) === 1 ? '' : 's'}`}>
            ×{task.rejection_count}
          </span>
        )}
      </td>
      <td>{formatDate(task.logged_at || task.created_at)}</td>
      <td title={task.created_by_name || 'Unknown creator'}>
        <span className="o-clip">{task.created_by_name || 'Unknown creator'}</span>
      </td>
    </tr>
  );
}
